import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass/styled-components';
import { FormSelect, Checkbox } from '@getro/rombo';

export const SelectField = ({ field, value, onChange, error, disabled }) => {
  if (field.allowMultiple) {
    const selectedValues = Array.isArray(value) ? value : [];

    const handleCheckboxChange = (optionId) => {
      if (!onChange) return;
      const newValues = selectedValues.includes(optionId)
        ? selectedValues.filter((v) => v !== optionId)
        : [...selectedValues, optionId];
      onChange({ target: { value: newValues } });
    };

    return (
      <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
        <Box
          sx={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'text.dark',
          }}
        >
          {field.label}
          {field.required && (
            <Box
              as="span"
              sx={{
                fontSize: '14px',
                fontStyle: 'italic',
                fontWeight: '400',
                color: 'text.subtle',
                ml: '8px',
              }}
            >
              Required
            </Box>
          )}
        </Box>
        {field.helperText && (
          <Box sx={{ fontSize: '14px', color: 'text.subtle', lineHeight: '20px' }}>{field.helperText}</Box>
        )}
        <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
          {field.options && field.options.length > 0 ? (
            field.options.map((option) => (
              <Checkbox
                key={option.id}
                label={option.label}
                checked={selectedValues.includes(option.id)}
                onChange={() => handleCheckboxChange(option.id)}
                disabled={disabled}
              />
            ))
          ) : (
            <Box sx={{ fontSize: '14px', color: 'text.subtle' }}>No options</Box>
          )}
        </Flex>
        {error && <Box sx={{ fontSize: '12px', color: 'danger', mt: '4px' }}>{error}</Box>}
      </Flex>
    );
  }

  const options =
    field.options?.map((opt) => ({
      value: opt.id,
      label: opt.label,
    })) || [];

  return (
    <FormSelect
      label={field.label}
      required={field.required}
      options={options}
      placeholder="Select an option"
      value={value}
      onChange={onChange}
      error={error}
      disabled={disabled}
      helperText={field.helperText}
      inputProps={{ style: { height: '48px' } }}
    />
  );
};

SelectField.propTypes = {
  field: PropTypes.shape({
    label: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    allowMultiple: PropTypes.bool,
    helperText: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string,
        label: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};

SelectField.defaultProps = {
  value: '',
  onChange: undefined,
  error: undefined,
  disabled: false,
};
